"use client";

import * as React from "react";
import { Search, Globe, Briefcase, Truck, ShieldCheck, Map, Package, Building2, ClipboardList, Landmark, Coins, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const tabsData = [{
  id: "sourcing",
  label: "Sourcing"
}, {
  id: "logistics",
  label: "Logistics"
}, {
  id: "warehousing",
  label: "Warehousing"
}, {
  id: "finance",
  label: "Finance"
}];
const servicesData = [{
  id: "src-1",
  tabId: "sourcing",
  title: "Verified Supplier Discovery",
  description: "Connect with pre-vetted manufacturers across India and the EU.",
  details: "Our rigorous verification process ensures you only deal with legitimate, high-quality suppliers. We check compliance, production capacity, and financial stability to minimize your cross-border sourcing risks.",
  iconName: "Search"
}, {
  id: "src-2",
  tabId: "sourcing",
  title: "International Buyer Network",
  description: "Expand your reach by connecting with active buyers in new markets.",
  details: "Access our extensive database of verified buyers actively looking for products from your region. We facilitate introductions and help you establish long-term B2B relationships.",
  iconName: "Globe"
}, {
  id: "src-3",
  tabId: "sourcing",
  title: "Market Entry Support",
  description: "Strategic guidance for establishing operations in foreign markets.",
  details: "Navigate the complexities of entering the EU or Indian market with our expert consultancy. We provide insights on local regulations, cultural nuances, and competitive landscapes.",
  iconName: "Briefcase"
}, {
  id: "log-1",
  tabId: "logistics",
  title: "Global Freight Forwarding",
  description: "End-to-end shipping solutions via air, sea, and land.",
  details: "Optimize your supply chain with our comprehensive freight services. We negotiate competitive rates and manage the entire transportation process from origin to destination.",
  iconName: "Truck"
}, {
  id: "log-2",
  tabId: "logistics",
  title: "Customs Clearance & Compliance",
  description: "Smooth navigation of import/export regulations and duties.",
  details: "Avoid costly delays with our expert customs brokerage. We ensure all documentation is accurate and compliant with the latest EU-India Free Trade Agreement regulations.",
  iconName: "ShieldCheck"
}, {
  id: "log-3",
  tabId: "logistics",
  title: "Route Optimization",
  description: "Data-driven logistics planning to reduce transit times and costs.",
  details: "Leverage advanced analytics to determine the most efficient shipping routes. We balance speed, cost, and environmental impact to deliver optimal logistics strategies.",
  iconName: "Map"
}, {
  id: "war-1",
  tabId: "warehousing",
  title: "EU Distribution Centers",
  description: "Strategic storage locations across the European Union.",
  details: "Position your inventory closer to your European customers. Our network of modern, secure warehouses enables faster fulfillment and reduced last-mile delivery costs.",
  iconName: "Building2"
}, {
  id: "war-2",
  tabId: "warehousing",
  title: "India Storage Solutions",
  description: "Scalable warehousing options in key Indian industrial hubs.",
  details: "Flexible storage solutions designed to accommodate fluctuating inventory levels. From bonded warehouses to temperature-controlled facilities, we meet your specific needs.",
  iconName: "Package"
}, {
  id: "war-3",
  tabId: "warehousing",
  title: "Inventory Management",
  description: "Real-time tracking and stock optimization services.",
  details: "Gain complete visibility into your supply chain with our integrated inventory management systems. Prevent stockouts and reduce holding costs with accurate forecasting.",
  iconName: "ClipboardList"
}, {
  id: "fin-1",
  tabId: "finance",
  title: "Trade Finance Solutions",
  description: "Letters of credit and working capital for international trade.",
  details: "Bridge the funding gap between manufacturing and delivery. We connect you with financial institutions offering competitive trade finance products tailored for cross-border transactions.",
  iconName: "Landmark"
}, {
  id: "fin-2",
  tabId: "finance",
  title: "Currency Exchange Support",
  description: "Favorable FX rates and hedging strategies for EUR/INR.",
  details: "Protect your profit margins from currency volatility. Access expert advice on foreign exchange strategies and secure competitive rates for your international payments.",
  iconName: "Coins"
}, {
  id: "fin-3",
  tabId: "finance",
  title: "Payment Protection",
  description: "Secure escrow services and payment guarantees.",
  details: "Trade with confidence using our secure payment frameworks. We mitigate the risk of non-payment or non-delivery, ensuring both buyers and sellers are protected.",
  iconName: "Shield"
}];
const iconMap: Record<string, React.ElementType> = {
  Search,
  Globe,
  Briefcase,
  Truck,
  ShieldCheck,
  Map,
  Building2,
  Package,
  ClipboardList,
  Landmark,
  Coins,
  Shield
};
export default function GrowthPathways() {
  return <section id="growth-pathways" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <h2 className="font-serif text-4xl font-bold tracking-tight text-primary-foreground sm:text-5xl mb-4">
            Growth Pathways
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Explore our comprehensive suite of services designed to simplify cross-border business between Europe and India.
          </p>
        </div>

        <Tabs defaultValue="sourcing" className="w-full">
          <div className="flex justify-center mb-12">
            <TabsList className="grid w-full max-w-md grid-cols-2 md:max-w-2xl md:grid-cols-4 h-auto p-1 bg-muted/50 border border-border/50 rounded-xl">
              {tabsData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="py-3 text-sm md:text-base rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>
          </div>

          {tabsData.map((tab, i) => <TabsContent key={tab.id} value={tab.id} data-index={i} className="focus-visible:outline-none focus-visible:ring-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out">
                {servicesData.filter(service => service.tabId === tab.id).map((service, j) => {
              const IconComponent = iconMap[service.iconName];
              return <Card key={service.id} data-index={j} className="bg-card border-border/40 shadow-lg hover:shadow-xl hover:border-primary/30 transition-all duration-300 flex flex-col h-full">
                        <CardHeader>
                          <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
                            {IconComponent && <IconComponent className="size-6" />}
                          </div>
                          <CardTitle className="text-xl text-card-foreground">
                            {service.title}
                          </CardTitle>
                          <CardDescription className="text-muted-foreground text-base mt-2">
                            {service.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex-grow">
                          {}
                        </CardContent>
                        <CardFooter className="pt-4 border-t border-border/20">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="w-full group">
                                Service Details
                                <Search className="ml-2 size-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[500px] bg-card border-border text-card-foreground">
                              <DialogHeader>
                                <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
                                  {IconComponent && <IconComponent className="size-6" />}
                                </div>
                                <DialogTitle className="text-2xl font-serif">
                                  {service.title}
                                </DialogTitle>
                                <DialogDescription className="text-base text-muted-foreground pt-2">
                                  {service.description}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                <p className="text-card-foreground leading-relaxed">
                                  {service.details}
                                </p>
                              </div>
                              <div className="flex justify-end pt-4 border-t border-border/20">
                                <Button variant="default">
                                  Inquire Now
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </CardFooter>
                      </Card>;
            })}
              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}